import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import './ConsultantSignup.css';

const TIER_LADDER = [
  {
    id: 'associate',
    name: 'SKIES Associate',
    rank: 1,
    canDeliver: 'Foundational level in any category',
    hours: '12h core + 6h per category',
    gating: 'Pass shadow-scored mock session',
  },
  {
    id: 'practitioner',
    name: 'SKIES Practitioner',
    rank: 2,
    canDeliver: 'Foundational + Advanced (all categories)',
    hours: '+8h per category, +4h CDC Watch Me!',
    gating: 'Pass 3 live sessions reviewed by a Lead',
  },
  {
    id: 'lead',
    name: 'SKIES Lead',
    rank: 3,
    canDeliver: 'All tiers including Mastery; mentors others',
    hours: '+12h Bayley-4 methodology, +6h report writing',
    gating: 'Quarterly inter-rater reliability check',
  },
];

const SOURCES = [
  {
    name: 'ASQ-3',
    role: 'Pre-session intake & parent-reported baseline',
    detail:
      'Five-domain parent screening (Communication, Fine Motor, Gross Motor, Problem Solving, Personal-Social) reviewed before every session to confirm tier-readiness.',
  },
  {
    name: 'Bayley-4',
    role: 'Direct-elicitation methodology',
    detail:
      'The gold standard for observation discipline: standardized presentation, neutral prompting, scoring on first independent response, item discontinue rules.',
  },
  {
    name: 'CDC Watch Me!',
    role: 'Red-flag literacy & referral pathway',
    detail:
      'Trains consultants to recognize when a child\'s pattern warrants a "talk to your pediatrician" note, and the warm, non-alarming language to use.',
  },
];

const CATEGORY_MODULES = [
  {
    name: 'Object Identification',
    weighting: 'Bayley-4 Cognitive (primary) · ASQ-3 Problem Solving (intake)',
    tiers: [
      { tier: 'Foundational', module: 'Receptive & expressive labeling: naming, pointing, function' },
      { tier: 'Advanced',     module: 'Relational reasoning: analogy, function-based grouping' },
      { tier: 'Mastery',      module: 'Multi-attribute filtering & abstraction (Bayley discontinue rule)' },
    ],
  },
  {
    name: 'Mathematics',
    weighting: 'Bayley-4 Cognitive (number sense) · ASQ-3 Problem Solving · CDC milestones at 36/48/60mo',
    tiers: [
      { tier: 'Foundational', module: 'Subitizing (seeing how many at a glance) & 1:1 correspondence' },
      { tier: 'Advanced',     module: 'Pattern reasoning & ordinal language (first, middle, last)' },
    ],
  },
  {
    name: 'Speech & Language',
    weighting: 'Bayley-4 Language (Receptive + Expressive) · ASQ-3 Communication · heaviest CDC Watch-Me weight',
    tiers: [
      { tier: 'Storytelling & Understanding', module: 'Story retelling & inferential reasoning' },
      { tier: 'Sounds & Early Reading',       module: 'Rhyme, phoneme isolation, multi-clause syntax' },
    ],
  },
];

const CROSS_CUTTING = [
  { hours: '3h',  name: 'ASQ-3 intake reading',         desc: 'How to interpret the parent\'s pre-session questionnaire and adjust elicitation.' },
  { hours: '6h',  name: 'Bayley-4 administration',       desc: 'Lead-only deep dive: neutral prompting, basal/ceiling rules, single re-presentation.' },
  { hours: '~2h', name: 'CDC Watch Me! certification',   desc: 'Free online; required before writing the "Developmental guidance" section of any report.' },
  { hours: '2h',  name: 'Cultural & linguistic responsivity', desc: 'When to use translated ASQ-3 forms; known Bayley-4 norm limitations for multilingual households.' },
  { hours: '3h',  name: 'Report writing',                desc: 'Translating observation into the parent-facing report using validated CDC wording.' },
];

export default function ConsultantSignup() {
  return (
    <>
      <Seo
        title="Become a SKIES Consultant"
        description="Join the Stratos Standard consultant network and help families celebrate their children's early achievements. Sign up to hear when applications open."
        path="/become-consultant"
      />
      <section className="coming-soon-page">
      {/* Background decoration */}
      <div className="cs-bg" aria-hidden="true">
        <div className="cs-circle cs-c1" />
        <div className="cs-circle cs-c2" />
        <div className="cs-circle cs-c3" />
      </div>

      <div className="container cs-inner">
        <div className="cs-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          Training Overview
        </div>

        <div className="cs-logo-area" aria-hidden="true">
          <svg width="72" height="72" viewBox="0 0 34 34" fill="none">
            <circle cx="17" cy="17" r="17" fill="#1a6bb5"/>
            <path d="M8 22 L17 8 L26 22" stroke="#d4a017" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M11 22 L23 22" stroke="#d4a017" strokeWidth="2.4" strokeLinecap="round"/>
            <circle cx="17" cy="8" r="2.2" fill="#f0c040"/>
          </svg>
        </div>

        <h1 className="cs-title">Become a SKIES Consultant</h1>
        <p className="cs-sub">
          Stratos consultants are trained on a custom framework grounded in three peer-validated sources:
          ASQ-3, Bayley-4, and the CDC&apos;s "Learn the Signs. Act Early." program.
        </p>

        {/* The three sources */}
        <div className="cs-section">
          <h2 className="cs-section-title">The Three Sources</h2>
          <div className="cs-source-grid">
            {SOURCES.map(s => (
              <div key={s.name} className="cs-source-card">
                <h3 className="cs-source-name">{s.name}</h3>
                <p className="cs-source-role">{s.role}</p>
                <p className="cs-source-detail">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Consultant credential ladder */}
        <div className="cs-section">
          <h2 className="cs-section-title">Consultant Certification Ladder</h2>
          <div className="cs-ladder">
            {TIER_LADDER.map(t => (
              <div key={t.id} className="cs-ladder-row">
                <div className="cs-ladder-rank">Tier {t.rank}</div>
                <div className="cs-ladder-body">
                  <h3 className="cs-ladder-name">{t.name}</h3>
                  <p className="cs-ladder-line"><span>Can deliver</span> {t.canDeliver}</p>
                  <p className="cs-ladder-line"><span>Training hours</span> {t.hours}</p>
                  <p className="cs-ladder-line"><span>Gating</span> {t.gating}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Per-category modules */}
        <div className="cs-section">
          <h2 className="cs-section-title">Per-Category Training Modules</h2>
          <div className="cs-cat-grid">
            {CATEGORY_MODULES.map(c => (
              <div key={c.name} className="cs-cat-card">
                <h3 className="cs-cat-name">{c.name}</h3>
                <p className="cs-cat-weighting">{c.weighting}</p>
                <ul className="cs-cat-tiers">
                  {c.tiers.map(t => (
                    <li key={t.tier}>
                      <span className="cs-cat-tier-name">{t.tier}</span>
                      <span className="cs-cat-tier-module">{t.module}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Cross-cutting modules */}
        <div className="cs-section">
          <h2 className="cs-section-title">Cross-Cutting Modules (All Consultants)</h2>
          <div className="cs-cross-list">
            {CROSS_CUTTING.map(m => (
              <div key={m.name} className="cs-cross-row">
                <span className="cs-cross-hours">{m.hours}</span>
                <div>
                  <p className="cs-cross-name">{m.name}</p>
                  <p className="cs-cross-desc">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Employment model */}
        <div className="cs-section">
          <h2 className="cs-section-title">Employment &amp; Onboarding</h2>
          <div className="cs-cross-list">
            <div className="cs-cross-row">
              <span className="cs-cross-hours">W-2</span>
              <div>
                <p className="cs-cross-name">You&apos;re an employee, not a contractor</p>
                <p className="cs-cross-desc">Stratos consultants are W-2 employees of our California entity. We withhold federal &amp; California taxes, pay the employer share of FICA, and carry workers&apos; compensation insurance from your first paid hour, fully compliant with California labor law.</p>
              </div>
            </div>
            <div className="cs-cross-row">
              <span className="cs-cross-hours">Square</span>
              <div>
                <p className="cs-cross-name">Onboarding &amp; payroll run on Square Payroll</p>
                <p className="cs-cross-desc">After you accept an offer, you&apos;ll get a Square Payroll invite to complete your I-9, federal W-4, California DE-4, and direct-deposit setup. Square handles your pay stubs, tax filings, and year-end W-2. Stratos never sees or stores your SSN or bank info.</p>
              </div>
            </div>
            <div className="cs-cross-row">
              <span className="cs-cross-hours">CA</span>
              <div>
                <p className="cs-cross-name">California protections you get</p>
                <p className="cs-cross-desc">Bi-weekly pay, itemized §226 wage statements, paid sick leave (CA HWHFA), meal/rest break compliance, §2802 expense reimbursement for required equipment, and no non-compete clauses (per Cal. Bus. &amp; Prof. Code §16600).</p>
              </div>
            </div>
            <div className="cs-cross-row">
              <span className="cs-cross-hours">Pay</span>
              <div>
                <p className="cs-cross-name">Hourly, non-exempt</p>
                <p className="cs-cross-desc">You&apos;re paid for session-hours plus admin and training time. Background check (Checkr) is required before your first session with a child.</p>
              </div>
            </div>
            <div className="cs-cross-row">
              <span className="cs-cross-hours">8h max</span>
              <div>
                <p className="cs-cross-name">No overtime: strict 8-hour daily cap</p>
                <p className="cs-cross-desc">To protect your wellbeing and keep sessions consistent for kids, the platform enforces a hard 8 hours/day and 40 hours/week limit. The scheduler will not let you accept a session that would put you over, and overtime is not authorized under any circumstance.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notify form */}
        <div className="cs-notify">
          <h3>Apply to Join the Network</h3>
          <p>Applications open monthly. Drop your name and we&apos;ll send the cohort schedule along with the W-2 offer-letter packet.</p>
          <form className="cs-form" onSubmit={e => { e.preventDefault(); e.target.reset(); alert('Thanks! We\'ll be in touch soon.'); }}>
            <input type="text"  className="form-input" placeholder="Your name"  required />
            <input type="email" className="form-input" placeholder="Your email" required />
            <button type="submit" className="btn btn-primary">Notify Me</button>
          </form>
        </div>

        <div className="cs-back">
          <Link to="/" className="btn btn-dark">← Back to Home</Link>
          <Link to="/contact" className="btn btn-outline-dark">Contact Us Instead</Link>
        </div>
      </div>
    </section>
    </>
  );
}
